/**
 * List logic for the header's organisation switcher (ticket 19), kept pure and
 * unit-tested the way `archive/filterLogic.ts` and
 * `services/provider/activeProposals.ts` are: the component renders, this
 * decides.
 */

export type Membership = {
  entityType: "building" | "service_provider";
  entityId: string;
  name: string;
  /** Providers carry none; a building's may be null. */
  address: string | null;
  registryCode: string | null;
};

export type CurrentEntity = { entityType: string; entityId: string } | null;

/**
 * Above this the list stops being a menu. A Radix `DropdownMenu` owns
 * typeahead, arrow keys and Space/Enter, and an input inside it fights all
 * three — so a longer list gets a `Dialog` with a filter instead (D10).
 */
export const MENU_MAX = 8;

/** Buildings first, then providers — each group already sorted by the server. */
export function groupMemberships(rows: Membership[]): {
  buildings: Membership[];
  providers: Membership[];
} {
  return {
    buildings: rows.filter((r) => r.entityType === "building"),
    providers: rows.filter((r) => r.entityType === "service_provider"),
  };
}

/**
 * Case- and diacritic-folded, so «parnu» finds «Pärnu mnt 123» — a list of
 * Estonian addresses typed on whatever keyboard the person has.
 */
function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Matches the name **or** the address: two buildings may share a name. */
export function filterMemberships(rows: Membership[], query: string): Membership[] {
  const needle = fold(query.trim());
  if (!needle) return rows;
  return rows.filter(
    (row) => fold(row.name).includes(needle) || fold(row.address ?? "").includes(needle)
  );
}

/** The row the session is acting as — disabled, with a check, never a request. */
export function isCurrent(row: Membership, current: CurrentEntity): boolean {
  return (
    !!current &&
    current.entityType === row.entityType &&
    current.entityId === row.entityId
  );
}

/**
 * `truncated` means the server capped the list, which is the management-company
 * case — exactly when a filter is needed most.
 */
export function usesDialog(rows: Membership[], truncated: boolean): boolean {
  return rows.length > MENU_MAX || truncated;
}
